import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessSectionProps {
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  steps?: ProcessStep[];
  serviceName?: string;
  variant?: "default" | "compact";
}

const ProcessSection = ({
  eyebrow: eyebrowProp,
  headline: headlineProp,
  subheadline: subheadlineProp,
  steps: stepsProp,
  serviceName,
}: ProcessSectionProps = {}) => {
  const { t } = useTranslation('home');

  const eyebrow = eyebrowProp || t('process.eyebrow');
  const headline = headlineProp || t('process.headline');
  const subheadline = subheadlineProp || t('process.subheadline');

  const defaultSteps: ProcessStep[] = [
    {
      number: "01",
      title: t('process.step1.title'),
      description: t('process.step1.description'),
    },
    {
      number: "02",
      title: t('process.step2.title'),
      description: t('process.step2.description'),
    },
    {
      number: "03",
      title: t('process.step3.title'),
      description: t('process.step3.description'),
    },
    {
      number: "04",
      title: t('process.step4.title'),
      description: t('process.step4.description'),
    },
    {
      number: "05",
      title: t('process.step5.title'),
      description: t('process.step5.description'),
    },
  ];

  const steps = stepsProp || defaultSteps;

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": serviceName
      ? `How to Get ${serviceName} in Houston`
      : "How to Get Your Roof Repaired or Replaced in Houston",
    "description": serviceName
      ? `${subheadline} Professional ${serviceName.toLowerCase()} services from Altum Contractors in Houston, TX.`
      : "A simple 5-step process for getting professional roofing services from Altum Contractors in Houston, TX. From free inspection to final walkthrough.",
    "totalTime": "P7D",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "0",
      "description": "Free inspection and estimate - no obligation"
    },
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      ...(index === 0 ? { "url": "https://www.altumcontractors.com/book-inspection" } : {})
    }))
  };

  const [headerVisible, setHeaderVisible] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.5 }
    );
    const itemsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setItemsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (itemsRef.current) itemsObserver.observe(itemsRef.current);

    return () => {
      headerObserver.disconnect();
      itemsObserver.disconnect();
    };
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <section
        ref={sectionRef}
        id="process"
        className="py-32 md:py-40 bg-slate-50 dark:bg-slate-800 overflow-hidden"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div
            ref={headerRef}
            className={`text-center mb-24 md:mb-32 transition-all duration-1000 ease-out ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-primary font-extrabold tracking-[0.2em] uppercase text-xs mb-4 block">
              {eyebrow}
            </span>
            <h2 className="text-secondary dark:text-white text-4xl md:text-5xl font-heading font-black uppercase tracking-tight">
              {headline}
            </h2>
            <p className="mt-6 text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              {subheadline}
            </p>
          </div>

          {/* Steps Grid - Horizontal on desktop with dashed timeline */}
          <div ref={itemsRef} className="relative grid gap-16 md:grid-cols-5 md:gap-4">
            {/* Dashed timeline connecting steps - centered through the numbers */}
            <div className="hidden md:block absolute top-24 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-slate-300 dark:border-slate-700 -z-0" />

            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative flex flex-col items-center text-center group z-10 transition-all duration-700 ease-out ${
                  itemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                {/* Large Outlined Number */}
                <div className="relative h-36 md:h-48 flex items-center justify-center mb-6">
                  <span className="step-number-outline font-heading font-black select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    {step.number}
                  </span>
                </div>

                {/* Step Title */}
                <h3 className="text-lg md:text-xl font-heading font-bold text-secondary dark:text-white mb-3">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed px-2 bg-slate-50 dark:bg-slate-900 relative inline-block">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProcessSection;
